/**
 * MODULE: domain/sales-intelligence/frontend/components/SalesIntelligenceFormModal.tsx
 * PURPOSE: Flowbite Pro compliant form-based research modal
 *
 * DEPENDENCIES:
 *   - flowbite-react: Modal, Button, Label, TextInput, Checkbox, Textarea, Badge, Alert
 *   - react-icons/hi: HiSearch, HiSparkles, HiX, HiCheckCircle, HiExclamation
 *   - ../hooks/useResearchWorkflow
 *
 * DESIGN: Flowbite Pro - spacing, typography, colors, dark mode
 * AGENT SAFETY: SAFE
 *
 * RECENT: 2026-02-10 – onComplete?: () => void (no-arg) passed to useResearchWorkflow so parent refetches after research completes; removed useEffect that called onComplete(insight). 2026-02-09 (Bug 4) – Show red "Please select at least one research area" when selectedCount === 0 (even when button disabled) so message is always visible. 2026-02-09 – Zero research areas validation on submit (zeroAreasError); Research complete UX: showResults, StreamedResearchCard fallback, View Company Profile; error banner and PipelineStatusPanel on failure; no helperText/isProcessing.
 *
 * See: /domain/sales-intelligence/README.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  TextInput,
  Checkbox,
  Textarea,
  Badge,
  Alert,
} from 'flowbite-react';
import {
  HiSearch,
  HiSparkles,
  HiX,
  HiCheckCircle,
  HiExclamation,
  HiEye,
} from 'react-icons/hi';
import { useResearchWorkflow } from '../hooks/useResearchWorkflow';
import { ResearchStatusPanel } from './ResearchStatusPanel';
import { PipelineStatusPanel } from './PipelineStatusPanel';
import { InsightDisplay } from './InsightDisplay';
import { buildCompanyDetailPath } from '../../../../_shared/frontend-shell/src/routes/builders';
import type { CompanyInsight } from '../../contracts/sales-intelligence.types';

/** Renders streamed research props when getInsight has not returned yet (or failed). */
function StreamedResearchCard({ props: p }: { props: Record<string, unknown> }) {
  const overview = (p?.overview as string) || '';
  const painPoints = (p?.pain_points as string[]) || [];
  const approach = (p?.approach as string) || '';
  const decisionMaker = (p?.decision_maker as string) || '';
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800 space-y-3 text-sm">
      {overview && <p className="text-gray-700 dark:text-gray-300">{overview}</p>}
      {painPoints.length > 0 && (
        <div>
          <span className="font-medium text-gray-900 dark:text-white">Pain points:</span>
          <ul className="list-disc list-inside mt-1 text-gray-600 dark:text-gray-400">{painPoints.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {approach && <p><span className="font-medium">Approach:</span> {approach}</p>}
      {decisionMaker && <p><span className="font-medium">Decision maker:</span> {decisionMaker}</p>}
    </div>
  );
}

interface SearchCriteria {
  overview: boolean;
  painPoints: boolean;
  decisionMaker: boolean;
  competitors: boolean;
  financials: boolean;
  recentNews: boolean;
  hiring: boolean;
  techStack: boolean;
  marketPosition: boolean;
}

const CRITERIA_CONFIG = [
  { key: 'overview' as const, label: 'Company Overview', description: 'Industry, size, business model', default: true },
  { key: 'painPoints' as const, label: 'Pain Points & Challenges', description: 'Current problems and obstacles', default: true },
  { key: 'decisionMaker' as const, label: 'Decision Makers', description: 'Key contacts and C-suite', default: true },
  { key: 'recentNews' as const, label: 'Recent News', description: 'Latest developments', default: true },
  { key: 'hiring' as const, label: 'Hiring Activity', description: 'Open roles and hiring signals', default: false },
  { key: 'competitors' as const, label: 'Competitors', description: 'Competitive landscape', default: false },
  { key: 'financials' as const, label: 'Financial Information', description: 'Revenue, funding, growth', default: false },
  { key: 'techStack' as const, label: 'Technology Stack', description: 'Tools and platforms', default: false },
  { key: 'marketPosition' as const, label: 'Market Position', description: 'Market share and trajectory', default: false },
];

const DEFAULT_CRITERIA: SearchCriteria = CRITERIA_CONFIG.reduce(
  (acc, item) => ({ ...acc, [item.key]: item.default }),
  {} as SearchCriteria
);

export interface SalesIntelligenceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefillCompany?: string;
  /** Called after research completes and insight is fetched; use to refetch parent data. */
  onComplete?: () => void;
  /**
   * When set (e.g. section refresh from CompanyPage), only these criteria are checked initially
   * and partialMerge is sent on run so other fields are not overwritten in DB.
   */
  forcedCriteriaKeys?: string[] | null;
  /** When true, send partialMerge to the research stream (pair with forcedCriteriaKeys). */
  partialSectionRefresh?: boolean;
}

export function SalesIntelligenceFormModal({
  open,
  onOpenChange,
  prefillCompany = '',
  onComplete,
  forcedCriteriaKeys = null,
  partialSectionRefresh = false,
}: SalesIntelligenceFormModalProps) {
  const [companyName, setCompanyName] = useState(prefillCompany);
  const [customPrompt, setCustomPrompt] = useState('');
  const [criteria, setCriteria] = useState<SearchCriteria>(DEFAULT_CRITERIA);

  const navigate = useNavigate();
  const { startResearch, isLoading, insight, error, progress, currentStep, output, reset, uiComponents } = useResearchWorkflow({
    onComplete,
  });

  // Fallback: last streamed CompanyResearchCard when complete but getInsight not yet (or failed)
  const lastResearchCard = uiComponents?.length
    ? [...uiComponents].reverse().find((c: { component?: string }) => c?.component === 'CompanyResearchCard')
    : null;
  const showResults = !isLoading && !error && (insight || lastResearchCard);

  useEffect(() => {
    if (prefillCompany && prefillCompany !== companyName) {
      setCompanyName(prefillCompany);
    }
  }, [prefillCompany]);

  useEffect(() => {
    if (!open) return;
    if (forcedCriteriaKeys && forcedCriteriaKeys.length > 0) {
      const next: SearchCriteria = { ...DEFAULT_CRITERIA };
      (Object.keys(next) as (keyof SearchCriteria)[]).forEach((k) => {
        next[k] = forcedCriteriaKeys.includes(k);
      });
      setCriteria(next);
    } else {
      setCriteria(DEFAULT_CRITERIA);
    }
  }, [open, forcedCriteriaKeys]);

  const handleCriteriaChange = (key: keyof SearchCriteria) => {
    setZeroAreasError(false);
    setCriteria((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [zeroAreasError, setZeroAreasError] = useState(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setZeroAreasError(false);
    if (!companyName.trim()) return;

    const selectedCriteria = Object.entries(criteria)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);

    if (selectedCriteria.length === 0) {
      setZeroAreasError(true);
      return;
    }

    await startResearch(companyName.trim(), {
      criteria: selectedCriteria,
      customPrompt: customPrompt.trim() || undefined,
      partialMerge: partialSectionRefresh === true,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    setZeroAreasError(false);
    setTimeout(() => {
      setCompanyName('');
      setCustomPrompt('');
      setCriteria(DEFAULT_CRITERIA);
      reset();
    }, 300);
  };

  const selectedCount = Object.values(criteria).filter(Boolean).length;

  return (
    <Modal show={open} onClose={handleClose} size="3xl">
      <ModalHeader className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
            <HiSparkles className="h-6 w-6 text-blue-600 dark:text-blue-300" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Sales Intelligence Research
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Get AI-powered company insights
            </p>
          </div>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-6 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
          {/* Company Name Input */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label htmlFor="company-name" className="text-sm font-medium">
                Company Name <span className="text-red-600">*</span>
              </Label>
              {companyName && (
                <Badge color="gray" size="sm">
                  Target: {companyName}
                </Badge>
              )}
            </div>
            <TextInput
              id="company-name"
              icon={HiSearch}
              placeholder="e.g., Stripe, HubSpot, Notion"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={isLoading}
              required
              color={error ? 'failure' : 'gray'}
            />
            {!companyName && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter a company name to begin research
              </p>
            )}
          </div>

          {/* Research Criteria */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <Label className="text-sm font-medium">Research Areas</Label>
              <Badge color="info" size="sm">
                {selectedCount} selected
              </Badge>
            </div>

            {(zeroAreasError || (selectedCount === 0 && !isLoading)) && (
              <p className="mb-2 text-sm text-red-600 dark:text-red-400" role="alert">
                Please select at least one research area.
              </p>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CRITERIA_CONFIG.map((item) => (
                <div
                  key={item.key}
                  className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                    criteria[item.key]
                      ? 'border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <Checkbox
                    id={item.key}
                    checked={criteria[item.key]}
                    onChange={() => handleCriteriaChange(item.key)}
                    disabled={isLoading}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor={item.key}
                      className="cursor-pointer text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {item.label}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Prompt */}
          <div>
            <Label htmlFor="custom-prompt" className="mb-2 text-sm font-medium">
              Custom Focus <span className="text-gray-500">(Optional)</span>
            </Label>
            <Textarea
              id="custom-prompt"
              placeholder="e.g., Focus on their recent AI product launches and API developer experience"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={3}
              disabled={isLoading}
              className="text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Add specific areas to explore or questions to answer
            </p>
          </div>

          {/* Research Status Panel (real-time progress when loading) */}
          {isLoading && (
            <>
              <ResearchStatusPanel
                isLoading={isLoading}
                progress={progress}
                currentStep={currentStep}
                output={output}
                error={error}
              />
              <PipelineStatusPanel isLoading={isLoading} companyName={companyName} />
            </>
          )}

          {/* Error state: show banner and pipeline debug (do not silently reset) */}
          {error && !isLoading && (
            <div className="space-y-3">
              <Alert color="failure" icon={HiExclamation}>
                <span className="font-medium">Research failed:</span> {error}
                <span className="block mt-2 text-sm font-normal">
                  Click &quot;Start Research&quot; to retry.
                </span>
              </Alert>
              <PipelineStatusPanel isLoading={false} companyName={companyName} />
            </div>
          )}

          {/* Results Display (when research completes: from API insight or streamed card) */}
          {showResults && (
            <div className="space-y-4">
              <Alert color="success" icon={HiCheckCircle}>
                <span className="font-medium">Research completed</span> for{' '}
                {insight?.company_name || (lastResearchCard?.props as { company_name?: string })?.company_name || companyName}
              </Alert>
              {insight && <InsightDisplay insight={insight} />}
              {!insight && lastResearchCard?.props && (
                <StreamedResearchCard props={(lastResearchCard.props as Record<string, unknown>)} />
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-t border-gray-200 dark:border-gray-700">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button color="gray" onClick={handleClose} disabled={isLoading} size="sm">
                <HiX className="mr-2 h-4 w-4" />
                Close
              </Button>
              {showResults && (
                <Button
                  color="blue"
                  size="sm"
                  onClick={() => {
                    handleClose();
                    const name = insight?.company_name || (lastResearchCard?.props as { company_name?: string })?.company_name || companyName;
                    const idForRoute = insight?.id ?? name;
                    navigate(buildCompanyDetailPath(idForRoute));
                  }}
                >
                  <HiEye className="mr-2 h-4 w-4" />
                  View Company Profile
                </Button>
              )}
            </div>
            {!showResults && (
              <Button
                type="submit"
                color="blue"
                disabled={isLoading || !companyName.trim() || selectedCount === 0}
                size="sm"
                className="min-w-[140px]"
              >
                {!isLoading && <HiSparkles className="mr-2 h-4 w-4" />}
                {isLoading ? 'Researching...' : 'Start Research'}
              </Button>
            )}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
